import React, { useContext, useState } from "react";

import { ZeosApiContext } from "../../context/ZeosApiContext";
import { IUtilityProfileState, useUtilityProfile } from "../../hooks/useUtilityProfile";
import {
  IServiceResolutionResult,
  UtilityAssignmentSource,
  UtilityResolutionStatus,
} from "../../services/zeos-api/contracts/UtilityIntelligence";

// UTL P04 (3898 Q1 / Q2): the utility assignment panel on a saved property. Read-only per service:
// utility, status, where it came from in plain language, and when. Re-check re-runs the territory
// lookup (POST) and keeps the current answer until the new one succeeds. Shown empty, never absent,
// before the first lookup. AMBIGUOUS services are answered in the selectors above, then saved.
// No override controls: admin override (P08) is deferred, and customers never see it.

const isSuccess = (status?: string) => status?.toLowerCase() === "success";

const SERVICE_LABELS: Record<string, string> = {
  electric: "Electric",
  natural_gas: "Gas",
  district_steam: "District steam",
  district_hot_water: "District hot water",
  district_chilled_water: "District chilled water",
  delivered_fuel: "Delivered fuel",
};

const serviceLabel = (code: string) => SERVICE_LABELS[code] ?? code;

export const sourceText = (source?: string | null) => {
  switch (source) {
    case UtilityAssignmentSource.UserSelected:
      return "Confirmed by you";
    case UtilityAssignmentSource.AdminOverride:
      return "Set by ZEOS support";
    case UtilityAssignmentSource.System:
      return "Found from the address";
    default:
      return "—";
  }
};

export const statusText = (service: IServiceResolutionResult) => {
  switch (service.status) {
    case UtilityResolutionStatus.Resolved:
      return service.utilityName || `Utility ${service.utilityId}`;
    case UtilityResolutionStatus.Ambiguous: {
      const names = (service.candidates ?? []).map((c) => c.utilityName).filter(Boolean);
      return names.length > 0
        ? `Needs confirmation: ${names.join(" or ")}`
        : "Needs confirmation";
    }
    case UtilityResolutionStatus.Unresolved:
      return "Not found yet. We're looking into it.";
    case UtilityResolutionStatus.OutOfCoverage:
      return "Outside our coverage area";
    case UtilityResolutionStatus.NoTerritoryModel:
      return "Supplied by a vendor, not a utility";
    default:
      return service.status;
  }
};

interface IProps {
  propertyId: number;
  /**
   * The stored profile and its refresh, from PropertyForm, shared with ServingUtilityFields so a
   * Re-check updates both. Omitted, the panel loads its own copy.
   */
  utilityProfile?: IUtilityProfileState;
}

export const PropertyUtilityPanel = ({ propertyId, utilityProfile }: IProps) => {
  const api = useContext(ZeosApiContext);
  const ownProfile = useUtilityProfile(propertyId, !utilityProfile);
  const { profile, loading, error: loadError, refreshProfile } = utilityProfile ?? ownProfile;
  const [checking, setChecking] = useState(false);
  const [checkError, setCheckError] = useState("");
  const error = checkError || loadError;

  const recheck = async () => {
    setChecking(true);
    setCheckError("");
    try {
      const response = await api.Territory.resolveProfileAsync(propertyId);
      if (isSuccess(response.zeosStatus)) {
        await refreshProfile();
      } else {
        // The stored answer is untouched when a re-check fails.
        setCheckError(response.message || "Could not check the utilities. Try again later.");
      }
    } catch {
      setCheckError("Could not check the utilities. Try again later.");
    } finally {
      setChecking(false);
    }
  };

  const services = profile?.services ?? [];
  const checkedOn = profile?.resolutionTimestamp ? profile.resolutionTimestamp.slice(0, 10) : null;
  const jurisdiction = profile?.jurisdiction;
  const place = [jurisdiction?.municipality, jurisdiction?.county, jurisdiction?.state]
    .filter(Boolean)
    .join(", ");

  return (
    <div className="col-span-12 px-2 mb-4" data-testid="property-utility-panel">
      <div className="border border-gray-200 rounded p-3">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-zeos-black">Serving utilities</h3>
          <button
            type="button"
            className="text-sm text-zeos-green underline disabled:opacity-50"
            onClick={recheck}
            disabled={checking || loading}
          >
            {checking ? "Checking…" : profile ? "Re-check" : "Look up utilities"}
          </button>
          {/* The button text is not announced when it changes; a live region is. */}
          <span className="sr-only" role="status">
            {checking ? "Checking…" : ""}
          </span>
        </div>

        {loading ? (
          <p className="text-sm text-zeos-black/70" role="status">Loading…</p>
        ) : !profile ? (
          <p className="text-sm text-zeos-black/70">
            Utilities haven&apos;t been looked up for this property yet.
          </p>
        ) : (
          <>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500">
                  <th className="py-1 pr-2 font-normal">Service</th>
                  <th className="py-1 pr-2 font-normal">Utility</th>
                  <th className="py-1 font-normal">Source</th>
                </tr>
              </thead>
              <tbody>
                {services.map((service) => (
                  <tr key={service.serviceCode} className="border-t border-gray-100">
                    <td className="py-1 pr-2">{serviceLabel(service.serviceCode)}</td>
                    <td
                      className={
                        "py-1 pr-2 " +
                        (service.status === UtilityResolutionStatus.Ambiguous ? "text-zeos-orange-dk" : "")
                      }
                    >
                      {statusText(service)}
                    </td>
                    <td className="py-1 text-zeos-black/70">{sourceText(service.assignmentSource)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-xs text-zeos-black/60 mt-2">
              {place ? `${place}. ` : ""}
              {checkedOn ? `Checked ${checkedOn}.` : ""}
              {services.some((s) => s.status === UtilityResolutionStatus.Ambiguous)
                ? " Choose the utility on your bill above and save to confirm it."
                : ""}
            </p>
          </>
        )}

        {error ? <p className="text-sm text-zeos-orange-dk mt-2">{error}</p> : null}
      </div>
    </div>
  );
};
